import type { ModuleAccessLevel } from '@/lib/rbacApi';

/**
 * Role & Access panel configuration (AR role-access redesign).
 *
 * Encodes the role/permission matrix the panel renders:
 * - the five assignable roles and their descriptions,
 * - admin-module defaults + locked flags per role,
 * - the active in-app (member) modules,
 * - Threads Advanced Tools defaults.
 *
 * Display-layer defaults only — persisted state (role assignment, per-user
 * overrides, threads advanced) flows through the existing RBAC API unchanged.
 */

export const PANEL_ROLE_SLUGS = [
  'SYSTEM_ADMIN',
  'ADMIN',
  'AGENCY_OWNER',
  'AGENCY_MANAGER',
  'ADVISOR',
] as const;

export type PanelRoleSlug = (typeof PANEL_ROLE_SLUGS)[number];

export const isPanelRoleSlug = (slug: string | null | undefined): slug is PanelRoleSlug =>
  !!slug && (PANEL_ROLE_SLUGS as readonly string[]).includes(slug);

export const isAdminRoleSlug = (slug: string | null | undefined): boolean =>
  slug === 'SYSTEM_ADMIN' || slug === 'ADMIN';

export const ROLE_DESCRIPTIONS: Record<PanelRoleSlug, string> = {
  SYSTEM_ADMIN: 'Full access to all tenant data; can manage agencies and feature flags.',
  ADMIN:
    'Full access to User Management & Content Management; other admin modules assigned per user.',
  AGENCY_OWNER: 'Member role. No admin access.',
  AGENCY_MANAGER: 'Member role. No admin access.',
  ADVISOR: 'Member role. No admin access.',
};

/** The five admin modules of the Role & Access panel (match adminNav section ids). */
export interface AdminModuleRow {
  key: string;
  label: string;
  description: string;
}

export const ADMIN_MODULES: AdminModuleRow[] = [
  {
    key: 'admin.user-management',
    label: 'User Management',
    description: 'User record table — roles, module access, advanced tools',
  },
  {
    key: 'admin.content-management',
    label: 'Content Management',
    description: 'Prompts and content configuration',
  },
  {
    key: 'admin.developer-controls',
    label: 'Developer Controls',
    description: 'Feature gates, MCP tools, skills, cost dashboard, agentic rollout',
  },
  {
    key: 'admin.audit-logs',
    label: 'Audit Logs',
    description: 'Admin action history and sync error logs',
  },
  {
    key: 'admin.exports',
    label: 'Exports',
    description: 'Threads export tooling',
  },
];

/** Admin modules locked to Full for the ADMIN role. */
const ADMIN_LOCKED_FULL = new Set(['admin.user-management', 'admin.content-management']);

export interface AdminModuleDefault {
  level: ModuleAccessLevel;
  locked: boolean;
}

/**
 * Default + locked state of an admin module for a role.
 * System Admin: Full on all five, locked. Admin: User Management + Content
 * Management Full locked, the other three No Access but assignable.
 * Member roles hold no admin modules (the panel renders a lockout message).
 */
export const adminModuleDefault = (
  roleSlug: string | null | undefined,
  moduleKey: string,
): AdminModuleDefault => {
  if (roleSlug === 'SYSTEM_ADMIN') return { level: 'full', locked: true };
  if (roleSlug === 'ADMIN') {
    if (ADMIN_LOCKED_FULL.has(moduleKey)) return { level: 'full', locked: true };
    return { level: 'none', locked: false };
  }
  return { level: 'none', locked: true };
};

/** Active in-app (member) modules shown by the panel. */
export const THREADS_MODULE_KEY = 'threads.workspace';
export const FINANCES_MODULE_KEY = 'finance.commissions';

/** Threads Advanced Tools default: ON for System Admin, OFF otherwise. */
export const threadsAdvancedDefault = (roleSlug: string | null | undefined): boolean =>
  roleSlug === 'SYSTEM_ADMIN';
